import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    Avatar,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Divider,
    useTheme
} from '@mui/material';
import {
    Person as PersonIcon,
    Add as AddIcon,
    Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import {
    fetchAssignments,
    updateAssignment,
    autoAssignEmployee,
    Assignment
} from '../../redux/slices/assignmentsSlice';
import { fetchStages } from '../../redux/slices/stagesSlice';
import { fetchEmployees } from '../../redux/slices/employeesSlice';
import { format } from 'date-fns';

interface AssignmentKanbanBoardProps {
    onViewAssignment: (assignmentId: number) => void;
    statusFilter?: string | null;
}

interface EmployeeOption {
    id: number;
    name: string;
    role: string;
}

const AssignmentKanbanBoard: React.FC<AssignmentKanbanBoardProps> = ({
    onViewAssignment,
    statusFilter = null
}) => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();

    // Get data from Redux store
    const { assignments, isLoading: assignmentsLoading } = useSelector((state: RootState) => state.assignments);
    const { stages, isLoading: stagesLoading } = useSelector((state: RootState) => state.stages);
    const { employees, isLoading: employeesLoading } = useSelector((state: RootState) => state.employees);

    // State for the assignment being dragged
    const [draggedAssignment, setDraggedAssignment] = useState<Assignment | null>(null);

    // State for the assignment dialog
    const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
    const [targetStageId, setTargetStageId] = useState<number | null>(null);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | ''>('');
    const [eligibleEmployees, setEligibleEmployees] = useState<EmployeeOption[]>([]);
    const [assignmentForDialog, setAssignmentForDialog] = useState<Assignment | null>(null);

    // State for loading during assignment operations
    const [isAssigning, setIsAssigning] = useState(false);

    // Filter assignments by status if statusFilter is provided
    const filteredAssignments = statusFilter
        ? assignments.filter(assignment => assignment.attributes?.status === statusFilter)
        : assignments;

    // Sort stages by order
    const sortedStages = [...stages].sort((a, b) => {
        const orderA = a.attributes?.order || 0;
        const orderB = b.attributes?.order || 0;
        return orderA - orderB;
    });

    // Group assignments by stage
    const assignmentsByStage = sortedStages.reduce<Record<number, Assignment[]>>((acc, stage) => {
        if (!stage?.id) return acc;

        acc[stage.id] = filteredAssignments.filter(
            assignment => assignment?.attributes?.stage?.data?.id === stage.id
        );

        return acc;
    }, {});

    // Handle the start of drag
    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, assignment: Assignment) => {
        setDraggedAssignment(assignment);

        // Set the drag image and data
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', JSON.stringify({
            assignmentId: assignment.id,
            currentStageId: assignment.attributes.stage?.data?.id
        }));

        // Add styling to the dragged element
        if (e.currentTarget.classList) {
            e.currentTarget.classList.add('dragging');
        }
    };

    // Handle the end of drag
    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
        // Remove styling from the dragged element
        if (e.currentTarget.classList) {
            e.currentTarget.classList.remove('dragging');
        }

        setDraggedAssignment(null);
    };

    // Handle dragging over a droppable area
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    // Handle dropping an assignment into a stage column
    const handleDrop = (e: React.DragEvent<HTMLDivElement>, stageId: number) => {
        e.preventDefault();

        try {
            const data = JSON.parse(e.dataTransfer.getData('text/plain'));
            const { assignmentId, currentStageId } = data;

            // Only proceed if dropping into a different stage
            if (currentStageId !== stageId && assignmentId) {
                // Find the assignment
                const assignment = assignments.find(a => a.id === assignmentId);
                if (!assignment) return;

                // Store the assignment for the dialog
                setAssignmentForDialog(assignment);

                // Find the stage
                const stage = stages.find(s => s.id === stageId);
                if (!stage) return;

                // Set the target stage for assignment
                setTargetStageId(stageId);

                // Filter eligible employees based on the required role for the stage
                const requiredRole = stage.attributes?.role?.data?.attributes?.name;
                const filtered = employees.filter(emp => {
                    if (!emp?.attributes) return false;

                    // If the stage doesn't have a required role, all employees are eligible
                    if (!requiredRole) return true;

                    // Check if the employee has the required role
                    return emp.attributes.role === requiredRole ||
                        (typeof emp.attributes.role === 'object' &&
                            emp.attributes.role?.data?.attributes?.name === requiredRole);
                }).map(emp => ({
                    id: emp.id,
                    name: `${emp.attributes?.Name || ''} ${emp.attributes?.lastName || ''}`.trim(),
                    role: typeof emp.attributes.role === 'string'
                        ? emp.attributes.role
                        : emp.attributes.role?.data?.attributes?.name || 'Unknown'
                }));

                setEligibleEmployees(filtered);

                // If there are eligible employees, open the assignment dialog
                // Otherwise, auto-assign
                if (filtered.length > 0) {
                    setIsAssignDialogOpen(true);
                } else {
                    // Auto-assign
                    handleAutoAssign();
                }
            }
        } catch (error) {
            console.error('Error handling drop:', error);
        }
    };

    // Handle manual assignment
    const handleManualAssign = async () => {
        if (!assignmentForDialog || !targetStageId || !selectedEmployeeId) {
            console.log('Missing required data:', { assignmentForDialog, targetStageId, selectedEmployeeId });
            setIsAssignDialogOpen(false);
            return;
        }

        try {
            setIsAssigning(true);
            const dressId = assignmentForDialog.attributes.dress.data.id;
            const projectId = assignmentForDialog.attributes.dress.data.attributes.project.data.id;
            
            console.log('Updating assignment with:', {
                id: assignmentForDialog.id,
                stage: targetStageId,
                employee: selectedEmployeeId,
                dress: dressId,
                project: projectId
            });

            // Update the assignment with the new stage and employee
            const result = await dispatch(updateAssignment({
                id: assignmentForDialog.id,
                assignmentData: {
                    data: {
                        stage: targetStageId,
                        employee: selectedEmployeeId,
                        dress: dressId,
                        project: projectId,
                        status: 'pending' // Reset status when reassigning
                    }
                }
            })).unwrap();

            console.log('Assignment update result:', result);

            // Clean up
            setIsAssignDialogOpen(false);
            setSelectedEmployeeId('');
            setTargetStageId(null);
            setAssignmentForDialog(null);

            // Refresh assignments after successful update
            await dispatch(fetchAssignments({
                page: 1,
                pageSize: 500,
                filters: {}
            }));
        } catch (error) {
            console.error('Error in manual assign:', error);
        } finally {
            setIsAssigning(false);
        }
    };

    // Handle auto assignment
    const handleAutoAssign = async () => {
        if (!assignmentForDialog || !targetStageId) {
            console.log('Missing required data:', { assignmentForDialog, targetStageId });
            setIsAssignDialogOpen(false);
            return;
        }

        try {
            setIsAssigning(true);
            console.log('Auto-assigning with:', {
                stageId: targetStageId,
                dressId: assignmentForDialog.attributes.dress.data.id,
                projectId: assignmentForDialog.attributes.dress.data.attributes.project.data.id
            });

            // Attempt to auto-assign an employee
            const result = await dispatch(autoAssignEmployee({
                stageId: targetStageId,
                dressId: assignmentForDialog.attributes.dress.data.id,
                projectId: assignmentForDialog.attributes.dress.data.attributes.project.data.id
            })).unwrap();

            console.log('Auto-assign result:', result);

            // Clean up
            setIsAssignDialogOpen(false);
            setSelectedEmployeeId('');
            setTargetStageId(null);
            setAssignmentForDialog(null);

            // Refresh assignments after successful update
            await dispatch(fetchAssignments({
                page: 1,
                pageSize: 500,
                filters: {}
            }));
        } catch (error) {
            console.error('Error in auto assign:', error);
        } finally {
            setIsAssigning(false);
        }
    };

    // Handle closing the assignment dialog
    const handleCloseDialog = () => {
        setIsAssignDialogOpen(false);
        setSelectedEmployeeId('');
        setTargetStageId(null);
        setAssignmentForDialog(null);
        setIsAssigning(false);
    };

    // Generate status chip based on status
    const getStatusChip = (status: string) => {
        switch (status) {
            case 'pending':
                return <Chip label="Pending" color="default" size="small" sx={{ mb: 1 }} />;
            case 'in_progress':
                return <Chip label="In Progress" color="primary" size="small" sx={{ mb: 1 }} />;
            case 'paused':
                return <Chip label="Paused" color="warning" size="small" sx={{ mb: 1 }} />;
            case 'completed':
                return <Chip label="Completed" color="success" size="small" sx={{ mb: 1 }} />;
            default:
                return null;
        }
    };

    // Get initials for avatar
    const getInitials = (Name: string, lastName: string) => {
        return `${Name.charAt(0)}${lastName.charAt(0)}`;
    };

    // Loading state
    if (stagesLoading || assignmentsLoading || employeesLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ overflowX: 'auto', pb: 2 }}>
            {/* Kanban Board Container */}
            <Box sx={{
                display: 'flex',
                minWidth: `${sortedStages.length * 320}px`,
                maxWidth: '100%',
                gap: 2,
                mb: 4,
                height: 'calc(100vh - 230px)',
            }}>
                {/* Map through stages to create columns */}
                {sortedStages.map(stage => {
                    if (!stage?.id || !stage?.attributes) return null;

                    const stageAssignments = assignmentsByStage[stage.id] || [];
                    const requiredRole = stage.attributes.role?.data?.attributes?.name;

                    return (
                        <Paper
                            key={stage.id}
                            elevation={2}
                            sx={{
                                width: 300,
                                display: 'flex',
                                flexDirection: 'column',
                                borderTop: `4px solid ${requiredRole ? theme.palette.primary.main : theme.palette.grey[500]
                                    }`,
                                '& > *:not(:first-of-type)': {
                                    mb: 1
                                }
                            }}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, stage.id)}
                        >
                            {/* Stage Header */}
                            <Box sx={{
                                p: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                                        {stage.attributes.name}
                                    </Typography>
                                    {requiredRole && (
                                        <Typography variant="caption" color="text.secondary">
                                            Required Role: {requiredRole}
                                        </Typography>
                                    )}
                                </Box>
                                <Chip
                                    label={stageAssignments.length}
                                    color="primary"
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>

                            {/* Assignment Cards Container */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    p: 2,
                                    '& > *:not(:last-child)': {
                                        mb: 2
                                    }
                                }}
                            >
                                {stageAssignments.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            textAlign: 'center',
                                            fontStyle: 'italic',
                                            p: 2
                                        }}
                                    >
                                        No assignments in this stage{statusFilter ? ` with status "${statusFilter}"` : ''}
                                    </Typography>
                                ) : (
                                    stageAssignments.map(assignment => {
                                        if (!assignment?.id || !assignment?.attributes) return null;

                                        // Extract the necessary data
                                        const dress = assignment.attributes.dress?.data?.attributes;
                                        const dressId = assignment.attributes.dress?.data?.id;
                                        const employee = assignment.attributes.employee?.data?.attributes;
                                        const employeeId = assignment.attributes.employee?.data?.id;

                                        return (
                                            <Card
                                                key={assignment.id}
                                                sx={{
                                                    boxShadow: 1,
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    '&:hover': {
                                                        transform: 'translateY(-3px)',
                                                        boxShadow: 3
                                                    },
                                                    cursor: 'grab',
                                                    '&.dragging': {
                                                        opacity: 0.5,
                                                        boxShadow: 6
                                                    }
                                                }}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, assignment)}
                                                onDragEnd={handleDragEnd}
                                                onClick={() => onViewAssignment(assignment.id)}
                                            >
                                                <CardContent>
                                                    {getStatusChip(assignment.attributes.status)}

                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                        {dress?.Name || 'Unnamed Dress'}
                                                    </Typography>

                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Type: {dress?.Type || 'Unknown'}
                                                    </Typography>

                                                    <Divider sx={{ my: 1 }} />

                                                    {employeeId ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                            <Avatar
                                                                sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    mr: 1,
                                                                    bgcolor: theme.palette.primary.main,
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {getInitials(employee?.Name || '', employee?.lastName || '')}
                                                            </Avatar>
                                                            <Typography variant="body2">
                                                                {employee?.Name} {employee?.lastName}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                            No assignee
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        );
                                    })
                                )}
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            {/* Assignment Dialog */}
            <Dialog open={isAssignDialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>Assign Task</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select an employee to assign this task or auto-assign based on availability and role requirements.
                    </Typography>

                    {assignmentForDialog && (
                        <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2 }}>
                            Dress: {assignmentForDialog.attributes.dress.data.attributes.Name}
                        </Typography>
                    )}

                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel id="employee-select-label">Employee</InputLabel>
                        <Select
                            labelId="employee-select-label"
                            id="employee-select"
                            value={selectedEmployeeId}
                            label="Employee"
                            onChange={(e) => setSelectedEmployeeId(e.target.value as number)}
                            disabled={isAssigning}
                        >
                            <MenuItem value="">
                                <em>Select an employee</em>
                            </MenuItem>
                            {eligibleEmployees.map(employee => (
                                <MenuItem key={employee.id} value={employee.id}>
                                    {employee.name} - {employee.role}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseDialog} 
                        color="inherit"
                        disabled={isAssigning}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAutoAssign}
                        color="secondary"
                        disabled={isAssigning}
                        startIcon={isAssigning ? <CircularProgress size={20} /> : null}
                    >
                        {isAssigning ? 'Assigning...' : 'Auto-Assign'}
                    </Button>
                    <Button
                        onClick={async () => {
                            setIsAssigning(true);
                            await handleManualAssign();
                        }}
                        color="primary"
                        disabled={!selectedEmployeeId || isAssigning}
                        startIcon={isAssigning ? <CircularProgress size={20} /> : null}
                    >
                        {isAssigning ? 'Assigning...' : 'Assign'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AssignmentKanbanBoard; 